import type { DateRange } from "../utils/date-ranges"

import { resolveDateRange } from "../utils/date-ranges"

/**
 * Query keys for admin dashboard endpoints
 * Hierarchical structure enables targeted cache invalidation
 */
function getDateRangeKey(dateRange: DateRange | 7 | 30): string {
  // Backward compatibility: convert numeric range to DateRange
  const range =
    typeof dateRange === "number" ? { type: "days" as const, days: dateRange } : dateRange
  const { startDate, endDate } = resolveDateRange(range)
  // Use ISO date strings for cache keys to ensure proper cache invalidation
  return `${startDate}_${endDate}`
}

export const adminQueryKeys = {
  root: ["admin"] as const,

  usage: (range: DateRange | 7 | 30) => ["admin", "usage", getDateRangeKey(range)] as const,

  topUsers: (range: DateRange | 7 | 30) => ["admin", "topUsers", getDateRangeKey(range)] as const,

  recentEvents: (limit: number) => ["admin", "recentEvents", limit] as const,

  trainingStatus: () => ["admin", "trainingStatus"] as const,

  trainingCatalog: () => ["admin", "trainingCatalog"] as const,

  trainingBaseAccess: () => ["admin", "training", "base", "access"] as const,
  trainingModuleAccess: (moduleId: string) =>
    ["admin", "training", "modules", moduleId, "access"] as const,
  trainingSectionAccess: (moduleId: string, sectionId: string) =>
    ["admin", "training", "modules", moduleId, "sections", sectionId, "access"] as const,
  trainingGating: () => ["admin", "training", "gating"] as const,

  dataSources: () => ["admin", "dataSources"] as const,

  segmentAnalysisConfig: () => ["admin", "segmentAnalysis", "config"] as const,

  personalSpacesSummary: (source: string) =>
    ["admin", "personalSpaces", "summary", source] as const,
  personalSpacesBatches: () => ["admin", "personalSpaces", "purgeBatches"] as const,
  personalSpacesSettings: () => ["admin", "personalSpaces", "settings"] as const,

  azureOverview: () => ["admin", "azureOverview"] as const,

  agentRuns: (range: DateRange | 7 | 30) => ["admin", "agentRuns", getDateRangeKey(range)] as const,

  agentSummary: (range: DateRange | 7 | 30) =>
    ["admin", "agentSummary", getDateRangeKey(range)] as const,

  navigatorLibraries: () => ["admin", "navigatorLibraries"] as const,

  // Library stats (on-demand per-library)
  libraryStats: (libraryId: string | null) => ["admin", "libraries", libraryId, "stats"] as const,

  libraryTreeCoverage: (libraryId: string | null) =>
    ["admin", "libraries", libraryId, "tree", "coverage"] as const,
  libraryTreeCoverageAll: () => ["admin", "libraries", "tree", "coverageAll"] as const,
  libraryTreeBackfillActive: (libraryId: string | null) =>
    ["admin", "libraries", libraryId, "tree", "backfill", "active"] as const,
  libraryTreeBackfillActiveAll: () =>
    ["admin", "libraries", "tree", "backfill", "activeAll"] as const,
  libraryTreeBackfillStatus: (libraryId: string | null, jobId: string | null) =>
    ["admin", "libraries", libraryId, "tree", "backfill", "status", jobId] as const,

  performanceStats: () => ["admin", "performanceStats"] as const,

  modelUsage: (range: DateRange | 7 | 30) =>
    ["admin", "modelUsage", getDateRangeKey(range)] as const,

  featureAdoption: (range: DateRange | 7 | 30) =>
    ["admin", "featureAdoption", getDateRangeKey(range)] as const,

  uploadStats: (range: DateRange | 7 | 30) =>
    ["admin", "uploadStats", getDateRangeKey(range)] as const,

  featureUsageStats: (range: DateRange | 7 | 30) =>
    ["admin", "featureUsageStats", getDateRangeKey(range)] as const,

  tokenMetrics: (range: DateRange | 7 | 30) =>
    ["admin", "tokenMetrics", getDateRangeKey(range)] as const,

  usageInsights: (range: DateRange | 7 | 30) =>
    ["admin", "usageInsights", getDateRangeKey(range)] as const,

  searches: (range: DateRange | 7 | 30) => ["admin", "searches", getDateRangeKey(range)] as const,

  costs: (range: DateRange | 7 | 30) => ["admin", "costs", getDateRangeKey(range)] as const,

  costsByUser: (range: DateRange | 7 | 30) =>
    ["admin", "costsByUser", getDateRangeKey(range)] as const,

  tenderRuns: (status: string, limit: number) =>
    ["admin", "tenders", "runs", status, limit] as const,
  tenderCases: (status: string, limit: number) =>
    ["admin", "tenders", "cases", status, limit] as const,
  tenderCaseDetail: (caseId: string | null, runId: string | null) =>
    ["admin", "tenders", "detail", caseId ?? "", runId ?? ""] as const,
  tenderUsageSummary: (days: number, currency: string) =>
    ["admin", "tenders", "usageSummary", days, currency] as const,

  // Tender Models
  tenderModels: () => ["admin", "tenderModels"] as const,

  // Image generation surface configuration + family catalog (#3038)
  imageModels: () => ["admin", "imageModels"] as const,

  // LLM Pool
  llmPoolConfig: () => ["admin", "llmPool", "config"] as const,
  llmPoolStats: () => ["admin", "llmPool", "stats"] as const,

  // Alert management
  alertConfig: () => ["admin", "alerts", "config"] as const,
  alertLogs: (filters?: Record<string, unknown>) =>
    ["admin", "alerts", "logs", filters ?? {}] as const,
  alertRecent: (days: number) => ["admin", "alerts", "recent", days] as const,

  // Runtime skill guidance
  skillGuidance: () => ["admin", "skills", "guidance"] as const,
  skillEnabled: () => ["admin", "skills", "enabled"] as const,

  // Presentation template layout catalog
  presentationTemplateCatalog: () => ["admin", "presentationTemplates", "catalog"] as const,
  presentationBrand: () => ["admin", "presentationBrand"] as const,

  // Slide Sense module config (presenter-mode availability)
  slideSenseConfig: () => ["admin", "slideSense", "config"] as const,

  // Audit Trail
  auditTrail: (filters?: {
    resource_type?: string
    resource_id?: string
    user_id?: string
    action?: string
    start_date?: string
    end_date?: string
    limit?: number
    offset?: number
  }) => ["admin", "auditTrail", filters ?? {}] as const,

  auditStats: (startDate?: string, endDate?: string) =>
    ["admin", "auditStats", startDate ?? "", endDate ?? ""] as const,
} as const
